import type { LucideIcon } from 'lucide-react';
import { motion } from 'framer-motion';
import { cn } from '@/shared/lib/cn';
import { Card } from '@/shared/ui/card';
import { Skeleton } from '@/shared/ui/skeleton';
import { Tooltip } from '@/shared/ui/tooltip';

interface KpiCardProps {
  label: string;
  value: string | number;
  icon: LucideIcon;
  hint?: string;
  trend?: { value: string; positive?: boolean };
  loading?: boolean;
  index?: number;
  className?: string;
}

export function KpiCard({
  label,
  value,
  icon: Icon,
  hint,
  trend,
  loading = false,
  index = 0,
  className,
}: KpiCardProps) {
  return (
    <motion.div
      className="h-full"
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05, type: 'spring', stiffness: 220, damping: 26 }}
    >
      <Card className={cn('flex h-full flex-col p-5', className)}>
        <div className="flex items-center justify-between gap-3">
          <span className="grid size-10 place-items-center rounded-xl bg-primary/12 text-primary">
            <Icon className="size-5" />
          </span>
          {trend ? (
            <span
              className={cn(
                'text-xs font-medium',
                trend.positive === false ? 'text-destructive' : 'text-success',
              )}
            >
              {trend.value}
            </span>
          ) : null}
        </div>
        <div className="mt-4 flex min-h-0 flex-1 flex-col">
          <div className="flex items-center gap-1.5">
            <p className="text-sm text-muted-foreground">{label}</p>
            {hint ? <Tooltip content={hint} /> : null}
          </div>
          {loading ? (
            <Skeleton className="mt-2 h-8 w-24" />
          ) : (
            <p className="mt-1 text-2xl font-semibold tracking-tight tabular-nums">{value}</p>
          )}
        </div>
      </Card>
    </motion.div>
  );
}
